import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/atoms/Button';
import './Pagination.scss';

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  className?: string;
}

/**
 * Page controls for a client-side paginated list: previous/next, numbered
 * pages (with ellipses when there are many) and "x–y of z".
 * Renders nothing when everything fits on one page.
 */
const Pagination: React.FC<PaginationProps> = ({ page, pageSize, totalItems, onPageChange, className = '' }) => {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  if (totalPages <= 1) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, totalItems);

  // 1 … 4 5 6 … 12
  const pages: (number | 'gap')[] = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || Math.abs(p - page) <= 1) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== 'gap') {
      pages.push('gap');
    }
  }

  return (
    <nav className={`pagination ${className}`} aria-label={t('pagination.label')}>
      <span className="pagination__summary">
        {t('pagination.summary', { first, last, total: totalItems })}
      </span>
      <div className="pagination__controls">
        <Button
          variant="ghost"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label={t('pagination.previous')}
        >
          ‹
        </Button>
        {pages.map((p, index) =>
          p === 'gap' ? (
            <span key={`gap-${index}`} className="pagination__gap">…</span>
          ) : (
            <Button
              key={p}
              variant={p === page ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => onPageChange(p)}
            >
              {p}
            </Button>
          )
        )}
        <Button
          variant="ghost"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label={t('pagination.next')}
        >
          ›
        </Button>
      </div>
    </nav>
  );
};

export default Pagination;
